import { Injectable } from '@angular/core';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { VideoUploadService } from '@solverml/api';
import { timeout, catchError, switchMap, take } from 'rxjs/operators';
import { interval, throwError } from 'rxjs';
import { lastValueFrom } from 'rxjs';

export interface VideoMetadata {
  title: string;
  description: string;
  gender?: string;
  category?: string;
  competition?: string;
  championship?: string;
}

export interface VideoUploadProgress {
  progress: number;
  status: 'pending' | 'uploading' | 'processing' | 'complete' | 'error';
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class VideosUploadService {

  constructor(private VideoUploadService: VideoUploadService) { }

  /**
   * Upload a video file to the server with metadata
   * @param file The video file to upload
   * @param metadata Video metadata (title, description, etc.)
   * @param progressCallback Callback function to track upload progress
   * @returns The video ID if upload is successful
   */
async uploadVideo(
  file: File,
  metadata: VideoMetadata,
  progressCallback: (progress: VideoUploadProgress) => void
): Promise<string> {
    try {
      // Set initial progress state
      progressCallback({
        progress: 0,
        status: 'pending',
        message: 'Preparando para subir el video...'
      });

      // 1. Initialize upload to get video_id
      const initResponse = await lastValueFrom(
        this.VideoUploadService.initializeVideoUpload({
          title: metadata.title,
          description: metadata.description,
          gender: metadata.gender,
          category: metadata.category,
          competition: metadata.competition,
          championship: metadata.championship,
          fileSize: file.size,
          fileName: file.name
        })
      );

      const videoId = initResponse.videoId;

      // 2. Prepare chunks
      const chunkSize = 50 * 1024 * 1024; // 50MB chunks
      const totalChunks = Math.ceil(file.size / chunkSize);

      // Upload each chunk
      for (let i = 0; i < totalChunks; i++) {
        const start = i * chunkSize;
        const end = Math.min(file.size, start + chunkSize);
        const chunk = file.slice(start, end);

        // Use the OpenAPI generated method for chunk upload
        await lastValueFrom(this.VideoUploadService.uploadChunk(chunk, videoId, i));

        // Update progress
        const progress = Math.round(((i + 1) / totalChunks) * 100);
        progressCallback({
          progress: progress,
          status: 'uploading',
          message: `Subiendo parte ${i+1} de ${totalChunks}...`
        });
      }

      // 3. Finalize the upload
      const endResponse = await lastValueFrom(
        this.VideoUploadService.finalizeVideoUpload(videoId)
      );

      console.log('Upload finalized:', endResponse);

      progressCallback({
        progress: 100,
        status: 'processing',
        message: 'Subida completa. Procesando el video...'
      });

      return videoId;
    } catch (error) {
      progressCallback({
        progress: 0,
        status: 'error',
        message: 'Error al subir el video: ' + (error.message || 'Error desconocido')
      });
      throw error;
    }
  }
}
